import { useContext, useEffect, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { IoMdRefresh } from "react-icons/io";
import LogicContext from "../../Context/LogicContext";
import GeneralCustomTable from "../../helpers/GeneralCustomTable";
import StatusBadge from "../../generals/StatusBadge";
import { subDepartmentService } from "../../services/subDepartment.service";
import SubDepartmentFormModal from "./SubDepartmentFormModal";

function compareDates(left, right) {
  const leftTime = Date.parse(left || "");
  const rightTime = Date.parse(right || "");
  if (Number.isNaN(leftTime)) return Number.isNaN(rightTime) ? 0 : -1;
  if (Number.isNaN(rightTime)) return 1;
  return leftTime - rightTime;
}

function hasActiveStatus(record) {
  return Number(record.statusId) === 1;
}

function subDepartmentToastMessage(message) {
  return /sub department/i.test(message) ? message : `Sub Department: ${message}`;
}

function SubDepartmentListPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logicContext = useContext(LogicContext);
  const [subDepartments, setSubDepartments] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedSubDepartments, setSelectedSubDepartments] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState("");

  const url = pathname.split("/");
  const basePath = `/${url[1]}/${url[2]}/${url[3] || "sub-departments"}`;

  async function loadSubDepartments() {
    setLoading(true);
    setError("");
    try {
      const result = await subDepartmentService.getSubDepartments();
      setSubDepartments(result.subDepartments);
      setDepartmentOptions(result.departmentOptions);
    } catch (loadError) {
      setError(
        loadError?.message
          ? subDepartmentToastMessage(loadError.message)
          : "Unable to load Sub Departments."
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateSubDepartment({ name, departmentId }) {
    setIsCreating(true);
    try {
      const result = await subDepartmentService.createSubDepartment(name, departmentId);
      logicContext.showToast(subDepartmentToastMessage(result.message), "success");
      await loadSubDepartments();
    } finally {
      setIsCreating(false);
    }
  }

  useEffect(() => {
    document.title = "Sub Department Setup";
    loadSubDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredSubDepartments = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return subDepartments.filter((record) => {
      const matchesSearch = String(record.name ?? "").toLowerCase().includes(search);
      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Active" && hasActiveStatus(record)) ||
        (statusFilter === "Inactive" && !hasActiveStatus(record));
      return matchesSearch && matchesStatus;
    });
  }, [subDepartments, searchText, statusFilter]);

  const columns = [
    {
      title: "Id",
      key: "id",
      dataIndex: "id",
      sorter: (a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0),
    },
    {
      title: "Name",
      key: "name",
      dataIndex: "name",
      className: "text-primary",
      sorter: (a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""),
      render: (value, row) => (
        <Link
          className="text-primary"
          to={`${basePath}/${row.id}`}
          onClick={(event) => event.stopPropagation()}
        >
          {value}
        </Link>
      ),
    },
    {
      title: "Department",
      key: "departmentName",
      dataIndex: "departmentName",
      sorter: (a, b) => (a?.departmentName ?? "").localeCompare(b?.departmentName ?? ""),
    },
    {
      title: "Status",
      key: "statusName",
      dataIndex: "statusName",
      sorter: (a, b) => (a?.statusName ?? "").localeCompare(b?.statusName ?? ""),
      render: (value, row) => (
        <StatusBadge statusId={hasActiveStatus(row) ? 1 : "I"} statusName={value} />
      ),
    },
    {
      title: "Created By",
      key: "createdBy",
      dataIndex: "createdBy",
      sorter: (a, b) => (a?.createdBy ?? "").localeCompare(b?.createdBy ?? ""),
    },
    {
      title: "Created At",
      key: "createdAt",
      dataIndex: "createdAt",
      sorter: (a, b) =>
        compareDates(a?.createdAtRaw ?? a?.createdAt, b?.createdAtRaw ?? b?.createdAt),
    },
  ];

  function handleRowClick(record) {
    navigate(`${basePath}/${record.id}`);
  }

  return (
    <div className="@container">
      <div className="@xl:flex @xl:justify-between">
        <div className="mt-4 flex flex-wrap gap-2">
          <div className="flex rounded bg-filterColor">
            <Dropdown>
              <Dropdown.Toggle
                style={{
                  backgroundColor: "#E5E5E5",
                  border: "none",
                  color: "#45444B",
                  fontSize: "12px",
                  height: "35px",
                }}
                id="sub-department-filter-dropdown"
              >
                FILTER BY SUB DEPARTMENT
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setStatusFilter("All")}>All</Dropdown.Item>
                <Dropdown.Item onClick={() => setStatusFilter("Active")}>Active</Dropdown.Item>
                <Dropdown.Item onClick={() => setStatusFilter("Inactive")}>Inactive</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <Tooltip title="Reload Sub Departments">
            <button
              type="button"
              onClick={loadSubDepartments}
              disabled={loading || isCreating}
              className="group flex h-[35px] items-center justify-center px-2 border-[1px] border-gray-200 bg-white rounded-md hover:bg-gray-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            >
              <IoMdRefresh className="h-6 w-6 text-gray-600 group-active:rotate-45 transition-all duration-300" />
            </button>
          </Tooltip>
          <input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            className="h-[35px] w-full @sm:w-[260px] rounded-md border border-gray-200 px-3 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Search Sub Departments by name"
          />
        </div>
        <div className="flex items-center">
          {selectedSubDepartments.length > 0 ? (
            <div className="mt-4 mr-2 text-sm text-gray-600">
              {selectedSubDepartments.length} Sub Departments selected
            </div>
          ) : null}
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={loading || isCreating}
            className="mt-4 py-2.5 px-6 text-sm rounded-md bg-blue-500 text-white font-medium focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
          >
            Add Sub Department
          </button>
        </div>
      </div>
      <div className="mt-5">
        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="h-8 rounded bg-gray-100" />
            <div className="mt-3 h-12 rounded bg-gray-100" />
            <div className="mt-3 h-12 rounded bg-gray-100" />
            <div className="mt-3 h-12 rounded bg-gray-100" />
          </div>
        ) : null}
        {!loading && error ? (
          <div className="rounded-lg border border-red-100 bg-red-50 p-5 text-sm text-red-700">{error}</div>
        ) : null}
        {!loading && !error && filteredSubDepartments.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">
            No Sub Departments found.
          </div>
        ) : null}
        {!loading && !error && filteredSubDepartments.length > 0 ? (
          <GeneralCustomTable
            tableHeadList={columns}
            tableDataList={filteredSubDepartments}
            allowCheckBox={true}
            setSelectedRows={setSelectedSubDepartments}
            rowKey={(record) => record.id}
            handleRowClick={handleRowClick}
            pageSizeOptions={["10", "25", "50"]}
          />
        ) : null}
      </div>
      {showCreateModal ? (
        <SubDepartmentFormModal
          mode="create"
          initialValues={{ name: "", departmentId: "", statusId: 1 }}
          departmentOptions={departmentOptions}
          onSubmit={handleCreateSubDepartment}
          onClose={() => setShowCreateModal(false)}
        />
      ) : null}
    </div>
  );
}

export default SubDepartmentListPage;
